import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { favoritesApi, productFavoritesApi, productsApi } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import './MobileFavoritesPage.css';

const POSTS_STORAGE_KEY = 'mobile-favorite-posts';

const resolveProductImage = (product) => {
  if (!product) return null;

  const imageValue =
    (Array.isArray(product.productImages) && product.productImages[0]?.url) ||
    (Array.isArray(product.images) ? product.images[0] : product.images) ||
    product.image ||
    product.imageUrl ||
    product.thumbnail ||
    null;

  if (!imageValue || typeof imageValue !== 'string') return null;
  if (imageValue.startsWith('http')) return imageValue;
  if (imageValue.startsWith('/uploads')) return imageValue;
  return `${API_BASE_URL}${imageValue.startsWith('/') ? imageValue : `/${imageValue}`}`;
};

const getProductPrice = (product) => {
  if (product?.price !== undefined && product?.price !== null && product.price !== '') {
    return Number(product.price);
  }
  if (product?.priceMin !== undefined && product?.priceMin !== null && product.priceMin !== '') {
    return Number(product.priceMin);
  }
  return null;
};

const getProductMoq = (product) => product?.moq || product?.minOrderQty || product?.minimumOrderQuantity || 1;

const getProductCountry = (product) => (
  product?.country ||
  product?.seller?.country ||
  product?.supplier?.country ||
  product?.originCountry ||
  'CN'
);

const getSellerYears = (product) => (
  product?.seller?.memberYears ||
  product?.seller?.yearsInBusiness ||
  product?.memberYears ||
  product?.yearsInBusiness ||
  1
);

const readFavoritePosts = () => {
  try {
    const rawValue = window.localStorage.getItem(POSTS_STORAGE_KEY);
    const parsedValue = rawValue ? JSON.parse(rawValue) : [];
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const getSupplierPreviewProducts = (supplier) => {
  if (Array.isArray(supplier?.products)) return supplier.products;
  if (Array.isArray(supplier?.productPreviews)) return supplier.productPreviews;
  if (Array.isArray(supplier?.previewProducts)) return supplier.previewProducts;
  return [];
};

const getSupplierProductCount = (supplier) => {
  if (Array.isArray(supplier?.products)) return supplier.products.length;
  if (typeof supplier?.products === 'number') return supplier.products;
  if (typeof supplier?._count?.products === 'number') return supplier._count.products;
  return 0;
};

const EmptyIllustrationBox = () => (
  <svg viewBox="0 0 180 140" fill="none" className="mf-empty-svg">
    <path d="M47 48h86l-11 49H58L47 48z" fill="#f2d29a" />
    <path d="M55 56h70l-5 18H60l-5-18z" fill="#f8e1ba" />
    <path d="M133 44l17 13" stroke="#8a96aa" strokeWidth="4" strokeLinecap="round" />
    <path d="M148 29l3 27" stroke="#8a96aa" strokeWidth="4" strokeLinecap="round" />
    <circle cx="71" cy="106" r="9" fill="#5a6473" />
    <circle cx="114" cy="106" r="9" fill="#5a6473" />
    <path d="M84 68c0-6 4.9-11 11-11 4.9 0 8.4 3.5 8.4 8.1 0 8.4-13.3 14.8-13.3 14.8S77 73.8 77 65.9c0-4.6 3.6-8.1 8.3-8.1 6.1 0 11 5 11 11z" fill="#ff7d5a" />
  </svg>
);

const EmptyIllustrationSupplier = () => (
  <svg viewBox="0 0 180 140" fill="none" className="mf-empty-svg">
    <rect x="58" y="25" width="64" height="90" rx="10" fill="#eef3fb" />
    <path d="M90 46c11 0 20 9 20 20v11H70V66c0-11 9-20 20-20z" fill="#dfe8f6" />
    <circle cx="90" cy="54" r="12" fill="#7f96c7" />
    <path d="M55 113h70" stroke="#dae3f3" strokeWidth="6" strokeLinecap="round" />
  </svg>
);

const EmptyIllustrationPosts = () => (
  <svg viewBox="0 0 180 140" fill="none" className="mf-empty-svg">
    <path d="M42 95h96" stroke="#edf1f7" strokeWidth="10" strokeLinecap="round" />
    <path d="M52 43h76a8 8 0 018 8v48H44V51a8 8 0 018-8z" fill="#e8f0fb" />
    <path d="M44 99h92" stroke="#dce6f5" strokeWidth="4" />
    <path d="M72 66h36" stroke="#bccae3" strokeWidth="6" strokeLinecap="round" />
    <path d="M62 80h56" stroke="#ccd8ee" strokeWidth="6" strokeLinecap="round" />
  </svg>
);

const MobileFavoritesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatPrice, formatPriceRange } = useCurrency();
  const [activeTab, setActiveTab] = useState('products');
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [favoriteSuppliers, setFavoriteSuppliers] = useState([]);
  const [favoritePosts, setFavoritePosts] = useState(() => readFavoritePosts());
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [loadingRecommended, setLoadingRecommended] = useState(true);

  const fetchFavoriteProducts = useCallback(async () => {
    if (!user?.id) {
      setFavoriteProducts([]);
      setLoadingProducts(false);
      return;
    }

    try {
      setLoadingProducts(true);
      const response = await productFavoritesApi.getByUserId(user.id, { page: 1, limit: 100 });
      setFavoriteProducts(response.favorites || []);
    } catch {
      setFavoriteProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [user?.id]);

  const fetchFavoriteSuppliers = useCallback(async () => {
    if (!user?.id) {
      setFavoriteSuppliers([]);
      setLoadingSuppliers(false);
      return;
    }

    try {
      setLoadingSuppliers(true);
      const response = await favoritesApi.getByUserId(user.id, { page: 1, limit: 100 });
      const suppliers = (response.favorites || []).filter((supplier) => !supplier.status || supplier.status === 'published' || supplier.status === 'approved');
      setFavoriteSuppliers(suppliers);
    } catch {
      setFavoriteSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  }, [user?.id]);

  const fetchRecommendedProducts = useCallback(async () => {
    try {
      setLoadingRecommended(true);
      const response = await productsApi.getAll({ limit: 8 });
      const products = Array.isArray(response) ? response : response?.products || [];
      setRecommendedProducts(products.filter((product) => product?.status !== 'draft').slice(0, 8));
    } catch {
      setRecommendedProducts([]);
    } finally {
      setLoadingRecommended(false);
    }
  }, []);

  const fetchFavoritePosts = useCallback(async () => {
    try {
      setLoadingPosts(true);
      setFavoritePosts(readFavoritePosts());
    } finally {
      setLoadingPosts(false);
    }
  }, []);

  useEffect(() => {
    fetchFavoriteProducts();
    fetchFavoriteSuppliers();
    fetchRecommendedProducts();
  }, [fetchFavoriteProducts, fetchFavoriteSuppliers, fetchRecommendedProducts]);

  useEffect(() => {
    if (activeTab === 'products') {
      fetchFavoriteProducts();
      return;
    }

    if (activeTab === 'suppliers') {
      fetchFavoriteSuppliers();
      return;
    }

    fetchFavoritePosts();
  }, [activeTab, fetchFavoriteProducts, fetchFavoriteSuppliers, fetchFavoritePosts]);

  useEffect(() => {
    const handleFocus = () => {
      if (activeTab === 'products') {
        fetchFavoriteProducts();
        return;
      }

      if (activeTab === 'suppliers') {
        fetchFavoriteSuppliers();
        return;
      }

      fetchFavoritePosts();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [activeTab, fetchFavoriteProducts, fetchFavoriteSuppliers, fetchFavoritePosts]);

  useEffect(() => {
    window.localStorage.setItem(POSTS_STORAGE_KEY, JSON.stringify(favoritePosts));
  }, [favoritePosts]);

  const favoriteProductIds = useMemo(() => favoriteProducts.map((favorite) => String(favorite.productId || favorite.product?.id)).filter(Boolean), [favoriteProducts]);

  const handleToggleProductFavorite = async (event, product) => {
    event.stopPropagation();
    if (!user?.id || !product?.id) return;

    try {
      await productFavoritesApi.toggle(user.id, product.id);
      const targetId = String(product.id);
      const isExisting = favoriteProductIds.includes(targetId);

      setFavoriteProducts((currentFavorites) => {
        if (isExisting) {
          return currentFavorites.filter((favorite) => String(favorite.productId || favorite.product?.id) !== targetId);
        }

        return [
          {
            productId: product.id,
            product,
          },
          ...currentFavorites,
        ];
      });
    } catch {
      // Keep UI stable on failure.
    }
  };

  const handleRemoveSupplierFavorite = async (event, supplierId) => {
    event.stopPropagation();
    if (!user?.id || !supplierId) return;

    try {
      await favoritesApi.remove(user.id, supplierId);
      setFavoriteSuppliers((currentSuppliers) => currentSuppliers.filter((supplier) => supplier.id !== supplierId));
    } catch {
      // Keep UI stable on failure.
    }
  };

  const productTabLabel = `Products (${favoriteProducts.length})`;
  const supplierTabLabel = `Suppliers (${favoriteSuppliers.length})`;

  const getDisplayPrice = (product) => {
    if (product?.priceMin !== undefined && product?.priceMax !== undefined && Number(product.priceMin) !== Number(product.priceMax)) {
      return formatPriceRange(product.priceMin, product.priceMax);
    }

    const singlePrice = getProductPrice(product);
    if (singlePrice === null || singlePrice === undefined || singlePrice === '') return null;
    return typeof singlePrice === 'number' ? formatPrice(singlePrice) : singlePrice;
  };

  return (
    <div className="mf-page">
      <div className="mf-shell">
        <header className="mf-header">
          <button type="button" className="mf-back" onClick={() => navigate(-1)} aria-label="Go back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="mf-title">My favorites</h1>
        </header>

        <div className="mf-tabs">
          <button type="button" className={`mf-tab ${activeTab === 'products' ? 'is-active' : ''}`} onClick={() => setActiveTab('products')}>
            {productTabLabel}
          </button>
          <button type="button" className={`mf-tab ${activeTab === 'suppliers' ? 'is-active' : ''}`} onClick={() => setActiveTab('suppliers')}>
            {supplierTabLabel}
          </button>
          <button type="button" className={`mf-tab ${activeTab === 'posts' ? 'is-active' : ''}`} onClick={() => setActiveTab('posts')}>
            Posts
          </button>
        </div>

        <main className="mf-content">
          {activeTab === 'products' && (
            <section className="mf-panel">
              {loadingProducts ? (
                <div className="mf-loading">Loading products…</div>
              ) : favoriteProducts.length === 0 ? (
                <>
                  <div className="mf-empty-card mf-empty-card--products">
                    <EmptyIllustrationBox />
                    <p className="mf-empty-title">No Favorites Yet!</p>
                    <button type="button" className="mf-primary-button" onClick={() => navigate('/all-categories')}>
                      Source by category
                    </button>
                  </div>

                  <section className="mf-recommended-section">
                    <h2 className="mf-section-title">Recommended for you</h2>
                    <div className="mf-product-grid">
                      {loadingRecommended && [1, 2, 3, 4].map((item) => <div key={item} className="mf-product-skeleton" />)}
                      {!loadingRecommended && recommendedProducts.map((product) => {
                        const productId = String(product.id);
                        const isFavorited = favoriteProductIds.includes(productId);
                        const image = resolveProductImage(product);
                        const displayPrice = getDisplayPrice(product);

                        return (
                          <article key={product.id} className="mf-product-card" onClick={() => navigate(`/mobile/product/${product.id}`)}>
                            <div className="mf-product-image-wrap">
                              {image ? (
                                <img src={image} alt={product.name || product.title || 'Product'} className="mf-product-image" />
                              ) : (
                                <div className="mf-product-image-placeholder" />
                              )}
                              <button
                                type="button"
                                className={`mf-favorite-button ${isFavorited ? 'is-active' : ''}`}
                                onClick={(event) => handleToggleProductFavorite(event, product)}
                                aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                              >
                                <svg viewBox="0 0 24 24" fill={isFavorited ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                                  <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                                </svg>
                              </button>
                            </div>
                            <div className="mf-product-content">
                              <p className="mf-product-name">{product.name || product.title || 'Product'}</p>
                              {displayPrice && <p className="mf-product-price">{displayPrice}</p>}
                              <p className="mf-product-meta">MOQ: {getProductMoq(product)}</p>
                              <p className="mf-product-meta">{getSellerYears(product)} yr · {getProductCountry(product)}</p>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                </>
              ) : (
                <div className="mf-product-grid">
                  {favoriteProducts.map((favorite) => {
                    const product = favorite.product;
                    if (!product) return null;

                    const image = resolveProductImage(product);
                    const displayPrice = getDisplayPrice(product);

                    return (
                      <article key={favorite.productId || product.id} className="mf-product-card" onClick={() => navigate(`/mobile/product/${product.id}`)}>
                        <div className="mf-product-image-wrap">
                          {image ? (
                            <img src={image} alt={product.name || 'Product'} className="mf-product-image" />
                          ) : (
                            <div className="mf-product-image-placeholder" />
                          )}
                          <button
                            type="button"
                            className="mf-favorite-button is-active"
                            onClick={(event) => handleToggleProductFavorite(event, product)}
                            aria-label="Remove from favorites"
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                            </svg>
                          </button>
                        </div>
                        <div className="mf-product-content">
                          <p className="mf-product-name">{product.name || 'Product'}</p>
                          {displayPrice && <p className="mf-product-price">{displayPrice}</p>}
                          <p className="mf-product-meta">MOQ: {getProductMoq(product)}</p>
                          <p className="mf-product-meta">{getSellerYears(product)} yr · {getProductCountry(product)}</p>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {activeTab === 'suppliers' && (
            <section className="mf-panel">
              {loadingSuppliers ? (
                <div className="mf-loading">Loading suppliers…</div>
              ) : favoriteSuppliers.length === 0 ? (
                <div className="mf-empty-card mf-empty-card--suppliers">
                  <EmptyIllustrationSupplier />
                  <p className="mf-empty-title">No favorite added!</p>
                </div>
              ) : (
                <div className="mf-supplier-list">
                  {favoriteSuppliers.map((supplier) => {
                    const previews = getSupplierPreviewProducts(supplier).slice(0, 3);
                    const productCount = getSupplierProductCount(supplier);
                    return (
                      <article key={supplier.id} className="mf-supplier-card" onClick={() => navigate(`/seller/${supplier.slug || supplier.id}`)}>
                        <div className="mf-supplier-main">
                          <div className="mf-supplier-avatar">
                            {supplier.logo ? <img src={supplier.logo} alt={supplier.name} /> : <span>{(supplier.name || 'S').charAt(0)}</span>}
                          </div>
                          <div className="mf-supplier-copy">
                            <h3>{supplier.name || supplier.companyName || 'Supplier'}</h3>
                            <p>{supplier.country || 'UAE'} · {supplier.businessType || 'Manufacturer'}</p>
                            <p>{supplier.reviews || 0} rating · {productCount} products</p>
                          </div>
                          <button
                            type="button"
                            className="mf-supplier-heart"
                            onClick={(event) => handleRemoveSupplierFavorite(event, supplier.id)}
                            aria-label="Remove supplier from favorites"
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                            </svg>
                          </button>
                        </div>

                        <div className="mf-supplier-previews">
                          {previews.length > 0 ? previews.map((product) => (
                            <div key={product.id} className="mf-supplier-preview">
                              {resolveProductImage(product) ? <img src={resolveProductImage(product)} alt={product.name} /> : <div className="mf-supplier-preview-placeholder" />}
                            </div>
                          )) : (
                            <div className="mf-supplier-empty-previews">No product preview</div>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {activeTab === 'posts' && (
            <section className="mf-panel">
              {loadingPosts ? (
                <div className="mf-loading">Loading posts…</div>
              ) : favoritePosts.length === 0 ? (
                <div className="mf-empty-card mf-empty-card--posts">
                  <EmptyIllustrationPosts />
                  <p className="mf-empty-title">No data</p>
                </div>
              ) : (
                <div className="mf-post-list">
                  {favoritePosts.map((post) => (
                    <article key={post.id} className="mf-post-card">
                      {post.image ? <img src={post.image} alt={post.title} className="mf-post-image" /> : <div className="mf-post-image-placeholder" />}
                      <div className="mf-post-content">
                        <h3>{post.title}</h3>
                        <p>{post.description}</p>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

export default MobileFavoritesPage;